import type { FormSummaryJSONObject } from '@ikegps/core-api';
import { computed, makeObservable } from 'mobx';
import { sort as timSort } from 'timsort';
import { Services } from '../services';

function sortByUpdatedAt(summaryA: FormSummaryJSONObject, summaryB: FormSummaryJSONObject): number {
	if (!summaryA.updatedAt) return 1;
	if (!summaryB.updatedAt) return -1;
	return summaryB.updatedAt.localeCompare(summaryA.updatedAt);
}

export class FormSummaryStore {
	constructor() {
		makeObservable(this);
	}

	refresh() {
		if (Services.Department.rest != null) {
			// Don't list stale form summaries for slow connections
			Services.Department.rest.formSummaries.init([]);

			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			Services.Department.rest.formSummaries.refetch();

			this.refreshArchived();
		}
	}

	refreshArchived() {
		if (Services.Department.rest != null) {
			Services.Department.rest.archivedFormSummaries.init([]);

			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			Services.Department.rest.archivedFormSummaries.refetch();
		}
	}

	get(revision: string): FormSummaryJSONObject | null {
		const rest = Services.Department.rest;
		return rest.Form.Summary.getByRevision(revision).value;
	}

	async getAsync(revision: string): Promise<FormSummaryJSONObject | null> {
		const rest = Services.Department.rest;
		return await rest.Form.Summary.getByRevision(revision).fetch();
	}

	@computed get isLoading(): boolean {
		if (Services.Department.current == null) {
			return false;
		}
		return Services.Department.rest.formSummaries.isFetching;
	}

	/**
	 * List of un-deleted latest form summaries deduped by form uuid
	 */
	@computed get all(): FormSummaryJSONObject[] {
		if (Services.Department.current == null) {
			return [];
		}

		const rest = Services.Department.rest;
		const summaries = rest.formSummaries.value;

		if (summaries == null) {
			return [];
		}

		// Already sorted from server but good to be careful
		timSort(summaries, sortByUpdatedAt);

		return summaries;
	}

	/**
	 * List of archived form summaries sorted by last updated
	 */
	@computed get archived(): FormSummaryJSONObject[] {
		if (Services.Department.current == null) {
			return [];
		}

		const rest = Services.Department.rest;
		const summaries = rest.archivedFormSummaries.value;

		if (summaries == null) {
			return [];
		}

		timSort(summaries, sortByUpdatedAt);

		return summaries;
	}
}
